/**
 * LanguageSelector Molecule
 *
 * A dropdown selector for switching application language.
 * Combines Button-like interaction with flag icons and routing logic.
 */

'use client';

import { useTranslation } from '@/app/i18n/client';
import BrazilFlag from '@/components/icons/BrazilFlag';
import SpainFlag from '@/components/icons/SpainFlag';
import UnitedStatesFlag from '@/components/icons/UnitedStatesFlag';
import theme from '@/theme';
import { ChevronDown } from 'lucide-react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface LanguageSelectorProps {
  dark?: boolean;
}

export default function LanguageSelector({ dark = false }: LanguageSelectorProps) {
  const { lng } = useParams() as { lng: string };
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation(lng);
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const languages = [
    { language: 'pt-BR', flag: <BrazilFlag />, name: 'pt' },
    { language: 'en', flag: <UnitedStatesFlag />, name: 'en' },
    { language: 'es', flag: <SpainFlag />, name: 'es' },
  ];
  const [selectLanguage, setSelectLanguage] = useState<any>(languages[0]);
  const textColor = dark ? 'text-white' : 'text-slate-900';
  const iconColor = dark ? (theme as any).colors.white : (theme as any).colors.slate[900];

  const toggleDropDown = () => {
    setShowDropDown(!showDropDown);
  };

  const dismissHandler = (event: React.FocusEvent<HTMLButtonElement>): void => {
    if (event.currentTarget === event.target) {
      setShowDropDown(false);
    }
  };

  const onClickHandler = (language: any): void => {
    if (!pathname) return;
    const splitPathname = pathname.split('/');
    splitPathname.splice(0, 2);
    router.push(`/${language.language}/${splitPathname.join('/')}`);
  };

  useEffect(() => {
    const currentLanguage = lng;
    const languageInRoute = languages.find((lang) => currentLanguage === lang.language);
    setSelectLanguage(languageInRoute || languages[0]);
  }, []);

  useEffect(() => {
    setShowDropDown(showDropDown);
  }, [showDropDown]);

  return (
    <div className='flex'>
      <button className='relative -mx-2 p-2' onClick={toggleDropDown} onBlur={(e) => dismissHandler(e)}>
        <div className='flex items-center justify-center'>
          <p className={twMerge('pr-1 font-medium', textColor)}>{t(selectLanguage.name)}</p>
          <ChevronDown color={iconColor} />
        </div>
        {showDropDown && (
          <div className='absolute top-10 right-2 z-20 min-w-max overflow-hidden border border-zinc-300/40 bg-white'>
            {languages.map((language) => (
              <div
                onClick={() => onClickHandler(language)}
                key={language.name}
                className='cursor-pointer border-zinc-300/40 px-3.5 py-1.5 hover:bg-zinc-300 [&:not(:last-child)]:border-b'>
                <div className='flex items-center gap-2'>
                  <p className='font-medium'>{t(language.name)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </button>
    </div>
  );
}

export { LanguageSelector };
